import type { World } from '../sim/types';
import { SURVEY_LABELS, plainTitle } from './surveyNames';
import { nextEvent } from '../sim/nextEvent';
import { escapeHtml } from './format';
import { managementOf, overviewOf, type Indicator } from './overview';
import { CHAPTERS, CHAPTER_NAMES, OBJECTIVES, ONGOING, currentObjective, guideSkipped, seasonOf } from '../sim/campaign';
import { taggedHtml } from './tagged';
import { ISLAND_STEPS, goalsOf, islandStep, nextIsland, recommendedGoal, type IslandStep } from './goals';
import { ISLAND_NAMES, type ReefIsland } from '../sim/reef';
import { changesHtml } from './journal';
import { tagRecords } from '../sim/tags';
import { civilFromEpochDay, dateLabel, epochDayOf } from '../sim/calendar';
import { atSea, boatName, dollars, freeBoat, freeLiaison, fundsLevel, FUNDS_MONTHS, volunteersOn } from '../sim/operations';
import { patrolMonthly } from './areasPanel';
import { ECONOMY } from '../sim/economy';
import type { Pane } from './sheet';

/**
 * The overhaul's shell content (M237): the objective card at the panel's head and Explore's pane, each markup from the
 * world and the HUD's own state, so it is tested without a browser; `hud.ts` sets it only when it changes.
 */

/** The three destinations' icons, inline and drawn in the text's color (M237: no image to fetch, nothing to credit). */
export const NAV_ICONS: Readonly<Record<Pane, string>> = {
  explore: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12c3.5-5 9-6.5 13-3.5 1.2.9 2.3 2.1 3 3.5-.7 1.4-1.8 2.6-3 3.5-4 3-9.5 1.5-13-3.5z"/><path d="M19 12l2.5-3v6z"/><circle cx="14.5" cy="11" r="1"/></svg>',
  manage: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 8.2-7 10-4-1.8-7-5.5-7-10V6z"/><path d="M9 12l2 2 4-4"/></svg>',
  world: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17M12 3.5c2.6 2.4 3.8 5.3 3.8 8.5s-1.2 6.1-3.8 8.5c-2.6-2.4-3.8-5.3-3.8-8.5S9.4 5.9 12 3.5z"/></svg>',
};

/** What the objective card tells: its kind, the step where it is one of several, what to do and why, and the way to do it. */
export interface Objective {
  /** The campaign's objective's id, where it is one (M262: to tell when it is met). */
  id?: string;
  label: string;
  step?: string;
  title: string;
  body: string;
  action?: { label: string; act: string };
  /** A choice of acts beside the action (M295: "While the boat is out"), each its label, a line of what it costs and when it acts, and its act. */
  choices?: { label: string; detail: string; act: string }[];
  /** The first chapter's guide, which a player may put away (M303, F2: "Skip the guide"). */
  guide?: true;
  /** A one-word status beside the habitat's name ("Stable", "Under heat stress"), where one is read. */
  status?: { text: string; tone: 'good' | 'warn' | 'bad' | 'quiet' };
  /**
   * What the objective waits on, where it waits (M262, the brief's section 5: "a clear progress indicator, the estimated
   * completion date, an Advance to completion option"): the epoch day it comes, what it is, and the share of the wait
   * gone where it has a start.
   */
  progress?: { day: number; what: string; share: number | null; text: string };
}

/** What an objective of the campaign's waits on (M262), read from the world; none where it waits on the player. */
export function waitOf(world: World, id: string, act: string | undefined): Objective['progress'] | undefined {
  const today = epochDayOf(world);
  const left = (day: number): string => `${day - today} ${day - today === 1 ? 'day' : 'days'}`;
  // A survey at sea, whatever objective asks for one, while no boat is free to launch another (the launch's button then
  // has nowhere to go); the one home first (M277).
  const job = freeBoat(world.operations) === null ? atSea(world.operations).sort((a, b) => (a.phaseEnds[0] as number) - (b.phaseEnds[0] as number))[0] : undefined;
  if (job?.survey && (id === 'baseline' || act === 'survey' || act === 'survey-coral' || act === 'advance')) {
    const back = job.phaseEnds[0] as number;
    const days = job.days ?? back - today;
    return { day: back, what: `${SURVEY_LABELS[job.survey]} home`, share: days > 0 ? Math.min(1, Math.max(0, 1 - (back - today) / days)) : null, text: `The ${boatName(job.boat)} is at sea: back ${dateLabel(back)}, in ${left(back)}.` };
  }
  if (act !== 'advance') return undefined;
  if (id === 'beginManagement') {
    const rule = world.operations.jobs.find((j) => j.kind === 'rule' && (j.phaseEnds[0] as number) > today);
    if (rule) {
      const end = rule.phaseEnds[0] as number;
      return { day: end, what: 'Consultation over', share: Math.min(1, Math.max(0, 1 - (end - today) / ECONOMY.rule.consultationDays)), text: `Your liaison is consulting the communities: done ${dateLabel(end)}, in ${left(end)}.` };
    }
  }
  if (id === 'meetTest') {
    const { from: season, end } = seasonOf(world.campaign);
    if (season > today) return { day: season, what: 'The warm season', share: null, text: `The warm season begins ${dateLabel(season)}, in ${left(season)}.` };
    // Under way and no heatwave yet (review M262's: the button fell back to the month's accounts, four presses to its
    // end): to its last day, an advance stopped early by a heatwave's warning, which meets the test.
    if (end > today) return { day: end, what: 'The warm season\'s end', share: Math.min(1, (today - season) / (end - season)), text: `The warm season runs to ${dateLabel(end)}, in ${left(end)}: a marine heatwave, or the season passing without one.` };
  }
  if (id === 'annualReview' && world.campaign.reviewDay > today) return { day: world.campaign.reviewDay, what: 'The annual review', share: null, text: `The review is held ${dateLabel(world.campaign.reviewDay)}, in ${left(world.campaign.reviewDay)}.` };
  return undefined;
}

/**
 * The objective of the moment (M239): the campaign's first chapter, "Maui Nui: The Reef at a Crossroads", the objective's
 * place in it, what to do and why, and the way to it; after it the sanctuary's goals (M278). Its status the threats'
 * level, in one word, as M237's.
 */
export function objectiveOf(world: World, indicators: readonly Indicator[] = overviewOf(world), island: ReefIsland | null = null): Objective {
  const s = world.sanctuaries[0];
  if (!s) return { label: 'No sanctuary', title: 'Nothing to manage yet', body: 'This conservancy holds no sanctuary.' };
  const threats = indicators.find((i) => i.id === 'threats');
  const tone = (level: string | undefined): 'good' | 'warn' | 'bad' | 'quiet' => (level === 'good' ? 'good' : level === 'fair' ? 'warn' : level === 'poor' ? 'bad' : 'quiet');
  // Under heat stress or not, in words with its cause (M262: "Pressured" stood unexplained); the threats' detail on Manage.
  const status = threats?.level === 'poor' ? { status: { text: 'Reef under heat stress', tone: tone(threats.level) } } : {};
  if (!s.coral || !s.fish) return { label: 'Visiting', title: `Explore ${s.name}`, body: 'Its water, light and plankton run day by day. No reef life is simulated here yet.', action: { label: 'Explore', act: 'explore' }, ...status };
  const now = currentObjective(world);
  // The first chapter's guidance kept for a beginner (M278, `docs/STRATEGIC_PLAY.md` 1.1 and 1.5: the reef, the coral's
  // report, a first survey and its findings); after it the sanctuary's goals, several at once (`goals.ts`).
  if (now && now.chapter === 'firstSurvey') {
    const list = OBJECTIVES[now.chapter];
    const step = `${now.index + 1} of ${list.length}`;
    const wait = waitOf(world, now.objective.id, now.objective.action?.act);
    // While the first survey is at sea (M295, the one-island roadmap's R5): an act on the water on its island, offered
    // before the wait.
    const out = now.objective.id === 'baseline' ? whileOut(world) : null;
    // Never without a way on (M319: the day a cleanup came home with the survey to sail next, the boat free, no advance
    // was offered and the card stood still): the next event's, where no survey is at sea.
    const ahead = wait ?? nextAhead(world);
    if (out) return { ...out, guide: true, label: `Chapter ${CHAPTERS.indexOf(now.chapter) + 1} · ${CHAPTER_NAMES[now.chapter]}`, step, ...(ahead ? { action: { label: `Or advance to: ${wait ? wait.what.toLowerCase() : eventWords(ahead.what)}`, act: 'advance' }, progress: ahead } : { action: { label: 'Or advance to the next event', act: 'advance' } }), ...status };
    return {
      id: now.objective.id, guide: true, label: `Chapter ${CHAPTERS.indexOf(now.chapter) + 1} · ${CHAPTER_NAMES[now.chapter]}`, step, title: now.objective.title, body: now.objective.why,
      // While it waits, the way to skip the wait in place of the action (a launch with the boat at sea has nowhere to go).
      ...(wait ? { action: { label: `Advance to: ${wait.what.toLowerCase()}`, act: 'advance' }, progress: wait } : now.objective.action ? { action: now.objective.action } : {}), ...status,
    };
  }
  // The year's later chapters lead the home card while they are open (M321, the casual year's H3: "A Difficult Choice",
  // "The First Test" and the review were never shown, the card turned to the goals after the first). GAME CHOICE: the
  // chapter's objective and its way; where the objective only waits (an advance), the goal recommended now its way
  // meanwhile, so a wait is never the only press. An island chosen keeps its own step; the guide put away, the goals.
  if (now && !island && !guideSkipped(world.campaign)) {
    const head = { id: now.objective.id, label: `Chapter ${CHAPTERS.indexOf(now.chapter) + 1} · ${CHAPTER_NAMES[now.chapter]}`, step: `${now.index + 1} of ${OBJECTIVES[now.chapter].length}`, title: now.objective.title };
    // Staying solvent is a wait too, to the season's end (its review: "Check the accounts" its only press for months);
    // its season over and missed, said so, the review next (it can no longer be met).
    const solvent = now.objective.id === 'staySolvent';
    const today = epochDayOf(world);
    const { end } = seasonOf(world.campaign);
    const missed = solvent && today >= end;
    const waits = now.objective.action?.act === 'advance' || solvent;
    const why = missed ? 'A month of the warm season closed in debt, so this cannot be met now; the year\'s review on its last day reads it.' : now.objective.why;
    const own = waitOf(world, now.objective.id, now.objective.action?.act)
      ?? (solvent ? (missed ? waitOf(world, 'annualReview', 'advance') : { day: end, what: 'The warm season\'s end', share: null, text: `The warm season runs to ${dateLabel(end)}, in ${end - today} days.` }) : undefined);
    const g = waits ? recommendedGoal(goalsOf(world)) : undefined;
    // The chapter's own wait kept as the card's progress, so an advance pressed for it (a chapter's card's) runs to it
    // (its review: it had run to the next event).
    if (g?.way) return { ...head, body: `${why} Meanwhile: ${g.title.charAt(0).toLowerCase()}${g.title.slice(1)}. ${g.state}`, action: g.way, ...(own ? { progress: own } : {}), ...status };
    const wait = own ?? (waits ? nextAhead(world) : undefined);
    return { ...head, body: why, ...(wait ? { action: { label: `Advance to: ${eventWords(wait.what)}`, act: 'advance' }, progress: wait } : now.objective.action ? { action: now.objective.action } : {}), ...status };
  }
  // An island chosen (M292, the one-island roadmap's R2): its own step, never another island's while one waits here.
  return { ...(island ? islandObjective(world, island) : goalsObjective(world)), ...status };
}

/**
 * "While the boat is out" (M295, the one-island roadmap's R5, the playtest's fifth point: "an early operational
 * accomplishment"): with the first survey at sea, the liaison and the budget offered an act on its island that acts on
 * the water now, a patrol of one stretch (from the day it is paid for) or volunteers trained (counting after their month).
 * None where the survey went to no island, or one of the two already runs or is set up there; the volunteers only with
 * a liaison free. The protected area stays the next choice, once the survey answers.
 */
function whileOut(world: World): Omit<Objective, 'label'> | null {
  // The first survey's island, at sea or waiting in the plan, before a cleanup's (M319: with the cleanup at sea and the
  // survey planned, Lānaʻi's acts were offered for a survey of Maui).
  const ops = world.operations;
  const island = atSea(ops).find((j) => j.island !== null && j.survey !== 'shoreCleanup')?.island
    ?? ops.plan.find((p) => p.island !== null && p.kind !== 'shoreCleanup')?.island
    ?? atSea(ops).find((j) => j.island !== null)?.island ?? null;
  const x = island ? managementOf(world)?.islands.find((y) => y.island === island) : undefined;
  if (!island || !x) return null;
  const volunteers = volunteersOn(ops, island);
  if (x.patrol.level > 0 || volunteers) return null;
  const name = ISLAND_NAMES[island];
  const days = ECONOMY.patrolTerms.at(-1) as number;
  const liaison = freeLiaison(ops) !== undefined;
  // A patrol only where it can be paid for (M295's review: in debt it is refused).
  const choices = [
    ...(ops.funds > 0 ? [{ label: `Patrol ${name} now`, detail: `About ${dollars(patrolMonthly(x.patrol.costPerYear[1] ?? 0))} a month for ${days} days; it acts the day it is paid for`, act: `while-patrol:${island}` }] : []),
    ...(liaison ? [{ label: `Train volunteers on ${name}`, detail: `${dollars(ECONOMY.community.costPerYear / 12)} a month; they count its grazing fish from ${dateLabel(epochDayOf(world) + ECONOMY.community.setupDays)}, no boat needed. Takes your liaison ${ECONOMY.community.setupDays} days: no protected area proposed until then`, act: `while-volunteers:${island}` }] : []),
  ];
  if (choices.length === 0) return null;
  // The wait's own id, so its "Done" is told when the survey comes home whichever way it went (M295's review).
  return { id: 'baseline', title: 'While the boat is out', body: `Your liaison and budget can act on ${name} now. A protected area is the next choice, once the survey answers.`, choices };
}

/** An event's name inside a sentence: its first letter lowered, an island's name kept (M317's review: "molokaʻi's patrol ends"; M319: "Coral survey home"). */
const eventWords = (what: string): string => (Object.values(ISLAND_NAMES).some((n) => what.startsWith(n)) ? what : `${what.charAt(0).toLowerCase()}${what.slice(1)}`);

/**
 * What comes next on the calendar, the next-event button's own reckoning (M317: the island's card skipped a coral survey
 * home in 11 days, waiting in the plan, for a consultation 118 days off).
 */
function nextAhead(world: World): Objective['progress'] | undefined {
  const e = nextEvent(world);
  if (!e) return undefined;
  const days = e.day - epochDayOf(world);
  const what = plainTitle(e.what);
  return { day: e.day, what, share: null, text: `${what}, ${dateLabel(e.day)}, in ${days} ${days === 1 ? 'day' : 'days'}.` };
}

/**
 * The card for one island (M292): its next step (`islandStep`) with the way to it; where nothing waits there, what runs
 * there and the next island with a step to take, the result shown before another island is recommended.
 */
function islandObjective(world: World, island: ReefIsland): Objective {
  const step = islandStep(world, island);
  const head = { id: `island:${island}:${step.key}`, label: ISLAND_NAMES[island], step: '' };
  if (step.way) return { ...head, title: step.title, body: `${step.state}${step.cost ? ` It takes ${step.cost}.` : ''}`, action: step.way };
  // Another island only while no trip waits in the boats' plan and the funds stand good (M317, G7: the first session's
  // probe was sent on to all four islands in one day, 9 trips and 3 patrols queued with 2 months in hand). GAME CHOICE:
  // one island's work at a time, as a small conservancy's one crew works; past it, the days run on to what comes home.
  const next = world.operations.plan.length === 0 && fundsLevel(world) === 'good' ? nextIsland(world, island) : null;
  if (next) return { ...head, title: step.title, body: `${step.state} Next island: ${ISLAND_NAMES[next]} (${islandStep(world, next).title}).`, action: { label: `Go to ${ISLAND_NAMES[next]}`, act: `area:${next}` } };
  // The goal ahead, always with its button (M317: "nothing waits" had none; and the sanctuary's goal under an island's
  // name had named another island, M292's review): what comes home next, or the next event.
  const wait = nextAhead(world);
  // Each cause named that holds (its review: the plan's alone was told while the money held the next island too).
  const planned = world.operations.plan.length > 0;
  const poor = fundsLevel(world) !== 'good';
  const causes = [planned ? 'the trips planned are under way' : '', poor ? `the funds hold ${FUNDS_MONTHS.good} months of running costs` : ''].filter(Boolean).join(' and ');
  const ahead = nextIsland(world, island) ? ` The next island waits until ${causes}.` : ' Nothing waits on another island either.';
  // The next event in the card's own wait line (`progress`), not the body too (its review: told twice).
  return { ...head, title: step.title, body: `${step.state}${ahead}`, ...(wait ? { action: { label: `Advance to: ${eventWords(wait.what)}`, act: 'advance' }, progress: wait } : { action: { label: 'Advance to the next event', act: 'advance' } }) };
}

/**
 * The goals' card (M278, `docs/STRATEGIC_PLAY.md` 1.2 to 1.6): the goal recommended now, where it stands, the way the
 * game recommends and the others to it, and the goals besides it; where none has a way now, the days run on to what
 * comes next.
 */
function goalsObjective(world: World): Objective {
  const goals = goalsOf(world);
  const met = goals.filter((g) => g.level === 'met').length;
  const g = recommendedGoal(goals);
  const besides = goals.filter((x) => x !== g && x.level !== 'met').map((x) => x.title.toLowerCase());
  const also = besides.length > 0 ? ` Also open: ${besides.join('; ')}.` : '';
  const head = { label: 'Your goals', step: `${met} of ${goals.length} met` };
  if (g?.way) return { id: `goal:${g.id}`, ...head, title: g.title, body: `${g.state} Recommended: ${g.way.label}. ${g.others}${also}`, action: g.way };
  // The days run on to what comes next: a survey home, a consultation over, the warm season, the annual review.
  const wait = waitOf(world, 'goals', 'advance') ?? waitOf(world, 'beginManagement', 'advance') ?? waitOf(world, 'meetTest', 'advance') ?? waitOf(world, 'annualReview', 'advance');
  return { id: 'goal:none', ...head, title: 'Watch the reef', body: `Each goal is met or waits on the days.${also} ${ONGOING}`, ...(wait ? { action: { label: `Advance to: ${wait.what.toLowerCase()}`, act: 'advance' }, progress: wait } : { action: { label: 'Advance to the next event', act: 'advance' } }) };
}

/**
 * Whether the card's objective changed because its last one was met (M262: "Done: ... Next: ..."), not because the world
 * did (a New game or a Load changes the objective without meeting it).
 */
export function objectiveMet(world: World, was: string | undefined, now: string | undefined): boolean {
  // A goal met (M278, review M278's: none told done) as a campaign objective is.
  if (was?.startsWith('goal:')) return was !== now && goalsOf(world).find((g) => `goal:${g.id}` === was)?.level === 'met';
  // An island's step taken (M292): the same island's card moved on to a later step; another island chosen is no step, nor
  // a step gone back (a patrol's term over, a count grown old; its review).
  if (was?.startsWith('island:')) {
    const [, island, from] = was.split(':');
    const [, still, to] = (now ?? '').split(':');
    return now?.startsWith('island:') === true && island === still && ISLAND_STEPS.indexOf(to as IslandStep['key']) > ISLAND_STEPS.indexOf(from as IslandStep['key']);
  }
  return was !== undefined && was !== now && world.campaign.done[was] !== undefined;
}

/** The objective card's markup. */
export function objectiveHtml(o: Objective): string {
  const status = o.status ? `<span class="status tone-${o.status.tone}">${escapeHtml(o.status.text)}</span>` : '';
  return `<div class="objective-head"><span class="objective-label">${escapeHtml(o.label)}</span>${o.step ? `<span class="objective-step">${escapeHtml(o.step)}</span>` : ''}${status}</div>
    <button class="objective-title" data-objective="toggle" aria-label="${escapeHtml(o.title)}: why">${escapeHtml(o.title)}<span class="objective-more" aria-hidden="true">ⓘ</span></button>
    <p class="objective-body">${escapeHtml(o.body)}${o.guide ? ' <button class="link objective-skip" data-objective="skip-guide">Skip the guide</button>' : ''}</p>
    ${o.progress ? `${o.progress.share === null ? '' : `<div class="progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(100 * o.progress.share)}"><span style="width:${Math.round(100 * o.progress.share)}%"></span></div>`}<p class="objective-wait">${escapeHtml(o.progress.text)}</p>` : ''}
    ${o.choices ? `<div class="objective-choices">${o.choices.map((c, k) => `<button class="${k === 0 ? 'primary ' : ''}choice-act" data-objective="${escapeHtml(c.act)}"><strong>${escapeHtml(c.label)}</strong><span>${escapeHtml(c.detail)}</span></button>`).join('')}</div>` : ''}
    ${o.action ? `<button class="${o.choices ? 'link' : 'primary'}" data-objective="${escapeHtml(o.action.act)}">${escapeHtml(o.action.label)}</button>` : ''}`;
}

/** Each habitat's depths, as the exhibit holds them (`exhibit.ts`): told on its card. */
const HABITAT_DEPTHS: Readonly<Record<string, string>> = {
  reef: 'Coral and reef fish, 0 to 30 m',
  slope: 'The Deep 7 and the shrimp, 30 to 400 m',
  blue: 'Open water over the deep, the surface to 200 m',
  twilight: 'The twilight zone, 200 to 1,000 m',
  deep: 'Below 1,000 m',
  overview: 'The whole tank, every habitat at once',
};

/**
 * Explore's pane (the brief's section 1.3: the habitats, the animals and the field guide, watching the ecosystem): a
 * card for each habitat, the one shown marked; the hour and Watch one day; the field guide; and the tagged animals,
 * each with its way to it. Without the 3D view (M288) the hour and Watch one day go, and the note says where the animals
 * are instead.
 */
export function exploreHtml(world: World, habitats: readonly { id: string; name: string }[], current: string, graphics = true): string {
  const cards = habitats.length > 0
    ? `<h3>Habitats</h3><ul class="habitat-cards">${habitats.map((h) => `<li><button class="habitat-card${h.id === current ? ' on' : ''}" data-habitat="${escapeHtml(h.id)}"${h.id === current ? ' aria-current="true"' : ''}><strong>${escapeHtml(h.name)}</strong><span>${escapeHtml(HABITAT_DEPTHS[h.id] ?? '')}</span></button></li>`).join('')}</ul>`
    : '';
  return `${cards}
    <h3>Observe</h3>
    <div class="explore-actions">
      ${graphics ? '<button data-act="observe-open">Time of day and Watch one day</button>' : ''}
      <button data-act="guide">Field guide</button>
    </div>
    <p class="note">${graphics ? 'Tap an animal in the water to follow it, read its card and tag it.' : 'Without the 3D view, the panel over the ocean lists each habitat\'s animals, with a way to tag them; the time of day and Watch one day need the 3D view.'}</p>
    <section class="tagged-list">${taggedHtml(tagRecords(world))}</section>
    ${changesHtml(world)}`;
}

/**
 * The year of the sanctuary's management a day falls in, its first year the first: counted by the calendar from the
 * day it was set up (review 29: counted as 365 days, it slipped a day each leap year, "Year 4" on 31 December 2028,
 * the third year's last day and its review's).
 */
export function managementYear(establishedEpochDay: number, epochDay: number): number {
  const [a, b] = [civilFromEpochDay(establishedEpochDay), civilFromEpochDay(epochDay)];
  const before = b.month < a.month || (b.month === a.month && b.day < a.day);
  return b.year - a.year - (before ? 1 : 0) + 1;
}
